import { useState } from 'react';
import type { ReactNode } from 'react';

interface EventImageCarouselProps {
  images: string[];
  alt: string;
  overlay?: ReactNode;
}

export default function EventImageCarousel({ images, alt, overlay }: EventImageCarouselProps) {
  const [idx, setIdx] = useState(0);
  const hasImages = images.length > 0;
  const current = hasImages ? images[Math.min(idx, images.length - 1)] : '';

  const go = (dir: number) => {
    if (images.length <= 1) return;
    setIdx((prev) => (prev + dir + images.length) % images.length);
  };

  return (
    <div className="h-44 overflow-hidden relative group/img bg-primary-100">
      {hasImages ? (
        <img
          src={current}
          alt={alt}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <i className="ri-calendar-event-line text-4xl text-primary-400" aria-hidden="true" />
        </div>
      )}

      {overlay}

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              go(-1);
            }}
            aria-label="Previous photo"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/45 text-white opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              go(1);
            }}
            aria-label="Next photo"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/45 text-white opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
          </button>

          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIdx(i);
                }}
                aria-label={`Photo ${i + 1}`}
                className={`rounded-full transition-all cursor-pointer ${
                  i === idx ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>

          <span className="absolute bottom-2 right-2 bg-black/55 text-white text-[10px] font-label px-2 py-0.5 rounded-full">
            {idx + 1}/{images.length}
          </span>
        </>
      )}
    </div>
  );
}